import { useMemo, useRef, useState, useEffect } from 'react';
import { cn } from '@cotera/client/v0/lib/utils';
import { Tooltip } from '@cotera/client/app/components';
import { TraceTimeline } from './trace-timeline';
import { TraceSpan } from './trace-span';
import {
  getTimeRange,
  calculateTimelineTicks,
  calculateSpanData,
  formatDuration,
  getStatusIndicatorClassName,
} from './utils';
import type { TraceChartProps, SpanData, TimeRange } from './types';

// Row component that renders both label and span visualization
interface TraceRowProps<T = any> {
  spanData: SpanData<T>;
  index: number;
  timeRange: TimeRange;
  showDuration: boolean;
  showSidebar: boolean;
  isHovered: boolean;
  onHover: (spanId: string | null) => void;
  onSpanClick?: (spanId: string) => void;
  renderSpan?: (
    spanData: SpanData<T>,
    index: number,
    timeRange: TimeRange
  ) => React.ReactNode;
}

const TraceRow = <T = any,>({
  spanData,
  index,
  timeRange,
  showDuration,
  showSidebar,
  isHovered,
  onHover,
  onSpanClick,
  renderSpan,
}: TraceRowProps<T>) => {
  return (
    <div className="contents">
      {/* Left column: Label */}
      {showSidebar && (
        <div
          className={cn(
            'flex items-center gap-2 h-6 px-3 cursor-pointer transition-colors border-r border-border',
            isHovered && 'bg-zinc-50'
          )}
          style={{ paddingLeft: `${12 + spanData.depth * 16}px` }}
          onMouseEnter={() => onHover(spanData.id)}
          onMouseLeave={() => onHover(null)}
          onClick={() => onSpanClick?.(spanData.id)}
        >
          {/* Status indicator */}
          <div
            className={cn(
              'w-2 h-2 rounded-full shrink-0',
              getStatusIndicatorClassName(spanData.status)
            )}
          />

          {/* Span name */}
          <span className="text-sm font-medium text-gray-900 truncate">
            {spanData.name}
          </span>
        </div>
      )}

      {/* Right column: Span visualization */}
      <div
        className={cn(
          'relative h-6 px-4 cursor-pointer transition-colors',
          isHovered && 'bg-zinc-50'
        )}
        onMouseEnter={() => onHover(spanData.id)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onSpanClick?.(spanData.id)}
      >
        {renderSpan ? (
          <div>{renderSpan(spanData, index, timeRange)}</div>
        ) : (
          <TraceSpan
            spanData={spanData}
            index={index}
            timeRange={timeRange}
            showDuration={showDuration}
            isHovered={isHovered}
            onHover={onHover}
            onSpanClick={onSpanClick}
          />
        )}
      </div>
    </div>
  );
};

export const TraceChart = <T = any,>({
  spans,
  className,
  height = 400,
  showTimeline = true,
  showDuration = true,
  showSidebar = true,
  autoScroll = false,
  renderSpan,
  onSpanClick,
}: TraceChartProps<T>) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hoveredSpan, setHoveredSpan] = useState<string | null>(null);

  const timeRange = useMemo(() => getTimeRange(spans), [spans]);
  const timelineTicks = useMemo(
    () => calculateTimelineTicks(timeRange),
    [timeRange]
  );
  const spanData = useMemo(
    () => calculateSpanData(spans, timeRange),
    [spans, timeRange]
  );
  // spanData is already flat - no need to flatten

  // Auto-scroll to bottom when spans change and autoScroll is enabled
  useEffect(() => {
    if (autoScroll && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, [spans, autoScroll]);

  const gridTemplateColumns = showSidebar ? '256px 1fr' : '1fr';

  return (
    <div className={cn('w-full', className)}>
      {/* Main chart container with grid layout */}
      <div
        className={cn('w-full bg-white overflow-hidden min-w-0')}
        style={{ height: `${height}px` }}
      >
        <div
          className="grid h-full"
          style={{ gridTemplateColumns, gridTemplateRows: 'auto 1fr' }}
        >
          {/* Header row */}
          {showSidebar && (
            <div className="px-4 items-center h-[28px] border-r border-b border-border bg-gray-50 flex align-text-bottom">
              <h3 className="text-sm font-semibold text-gray-900">Traces</h3>
            </div>
          )}

          <div className="relative border-b border-border">
            {/* Timeline header - only spans right column */}
            <TraceTimeline
              timeRange={timeRange}
              timelineTicks={timelineTicks}
              showTimeline={showTimeline}
            />
          </div>

          {/* Single unified scroll container spanning both columns */}
          <div
            ref={scrollContainerRef}
            className="overflow-y-auto overflow-x-hidden"
            style={{
              gridColumn: showSidebar ? '1 / -1' : '1',
            }}
          >
            {spanData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                No spans to display
              </div>
            ) : (
              <div
                className="grid content-start"
                style={{
                  gridTemplateColumns,
                }}
              >
                {spanData.map((span, index) => (
                  <TraceRow
                    key={span.id}
                    spanData={span}
                    index={index}
                    timeRange={timeRange}
                    showDuration={showDuration}
                    showSidebar={showSidebar}
                    isHovered={hoveredSpan === span.id}
                    onHover={setHoveredSpan}
                    onSpanClick={onSpanClick}
                    renderSpan={renderSpan}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Hover tooltip */}
        {hoveredSpan && (
          <Tooltip
            tooltipContent={
              <div className="max-w-xs">
                {(() => {
                  const span = spans.find((s) => s.id === hoveredSpan);
                  if (span === undefined) {
                    return null;
                  }

                  return (
                    <div className="space-y-2">
                      <div className="font-medium text-white">{span.name}</div>
                      <div className="text-sm text-gray-200">
                        <div>
                          Duration:{' '}
                          {formatDuration(span.duration, timeRange.unit)}
                        </div>
                        <div>Status: {span.status}</div>
                        {span.data &&
                          typeof span.data === 'object' &&
                          'tags' in span.data &&
                          Array.isArray(span.data.tags) &&
                          span.data.tags.length > 0 && (
                            <div className="mt-2">
                              <div className="font-medium mb-1">Tags:</div>
                              {span.data.tags.map(
                                (tag: string, index: number) => (
                                  <div key={index} className="text-xs">
                                    {tag}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            }
            side="top"
          >
            <div />
          </Tooltip>
        )}
      </div>
    </div>
  );
};
